import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { formatCurrency } from "../../../../utils/formatCurrency";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotInsetCardStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";

const summaryShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

const STEP_COPY = {
  1: "Os dados preenchidos aqui alimentam a simulação e o destino final da cotação.",
  2: "Acompanhe o mix de veículos e o melhor frete conforme a simulação retorna.",
  3: "Revise o consolidado e confirme a proposta final antes de salvar.",
};

const SummarySection = ({ title, children }) => (
  <div className="summary-panel-section" style={autopilotInsetCardStyle}>
    <div className="summary-panel-section__header">{title}</div>
    <div className="summary-panel-section__body">{children}</div>
  </div>
);

const Val = ({ children, empty }) =>
  empty ? (
    <span className="summary-item-value empty">—</span>
  ) : (
    <span className="summary-item-value">{children}</span>
  );

const SummaryPanel = ({
  step,
  formData,
  veiculos,
  proposals,
  transportadoraEscolhida,
  freteTotal,
  servicos = [],
  somaServicos = 0,
  // PO1-3303 (H1): cotação por caixas. `caixas` já vem descrita pelo index.
  porCaixas = false,
  caixas = [],
  resumoCaixas = "",
  pesoReferencia = null,
}) => {
  const hasSimulated = proposals.length > 0;
  const cidadeUf = [formData.cidade, formData.uf].filter(Boolean).join(" / ");

  // PO1-2902/2903: o peso da proposta escolhida; antes de escolher, o da mais
  // barata, que é a que a tela destaca. Só existe em proposta de tabela.
  const propostaReferencia =
    transportadoraEscolhida ||
    (proposals.length > 0
      ? proposals.reduce((m, p) => (p.valor < m.valor ? p : m), proposals[0])
      : null);
  const pesoCubadoEscolhido = propostaReferencia?.pesoCubado ?? null;
  const pesoConsideradoEscolhido = propostaReferencia?.pesoConsiderado ?? null;
  const formatPeso = (kg) =>
    Number(kg).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getVeiculoFrete = (v) => {
    if (!transportadoraEscolhida) return v.freteSimulado;
    const match = v.proposals.find(
      (p) => p.transportadora === transportadoraEscolhida.transportadora
    );
    return match?.valor ?? null;
  };

  return (
    <Card className="card-everlog-shell summary-panel" style={summaryShellStyle}>
      <CardHeader className="summary-panel__header" style={autopilotHeaderStyle}>
        <div style={autopilotEyebrowStyle}>Detalhe lateral</div>
        <div style={autopilotTitleStyle}>Resumo da cotação</div>
        <p style={autopilotDescriptionStyle}>{STEP_COPY[step] || STEP_COPY[1]}</p>
      </CardHeader>

      <CardBody className="summary-panel__body" style={autopilotBodyStyle}>
        <div className="summary-panel__stack">
          <SummarySection title="Destino">
            <div className="summary-item">
              <div className="summary-item-label">Destinatário</div>
              <Val empty={!formData.destinatario}>{formData.destinatario}</Val>
            </div>
            <div className="summary-item">
              <div className="summary-item-label">Destino</div>
              <Val empty={!cidadeUf}>{cidadeUf}</Val>
            </div>
            <div className="summary-item">
              <div className="summary-item-label">Valor da NF</div>
              <Val empty={!formData.valorNF}>{formData.valorNF}</Val>
            </div>
          </SummarySection>

          {/* PO1-2903 (H2): peso real, volume e — depois de simular — o peso
              cubado que a tabela escolhida efetivamente cobrou. Sem isso o
              usuário não tem como saber por qual peso está pagando. */}
          <SummarySection title="Carga">
            {porCaixas ? (
              <>
                <div className="summary-item">
                  <div className="summary-item-label">Volumes</div>
                  <Val empty={!resumoCaixas}>{resumoCaixas}</Val>
                </div>
                {caixas.map((c, i) => (
                  <div className="summary-caixa-item" key={c.id != null ? c.id : i}>
                    {c.descricao}
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="summary-item">
                  <div className="summary-item-label">Peso real</div>
                  <Val empty={!formData.peso}>
                    {formData.peso ? `${formData.peso} kg` : ""}
                  </Val>
                </div>
                <div className="summary-item">
                  <div className="summary-item-label">Volume</div>
                  <Val empty={!formData.volume}>
                    {formData.volume ? `${formData.volume} m³` : ""}
                  </Val>
                </div>
              </>
            )}
            {pesoCubadoEscolhido != null && (
              <div className="summary-item">
                <div className="summary-item-label">Peso cubado</div>
                <Val>{`${formatPeso(pesoCubadoEscolhido)} kg`}</Val>
              </div>
            )}
            {pesoConsideradoEscolhido != null && (
              <div className="summary-item">
                <div className="summary-item-label">Peso cobrado</div>
                <Val>{`${formatPeso(pesoConsideradoEscolhido)} kg`}</Val>
              </div>
            )}
          </SummarySection>

          {step >= 2 && (
            <SummarySection title="Veículos">
              {veiculos.map((v, i) => {
                const freteVeiculo = getVeiculoFrete(v);
                return (
                  <div className="summary-vehicle-item" key={v.id}>
                    <div className="summary-vehicle-name">
                      {v.tipoVeiculo?.label || "—"}{" "}
                      {v.quantidade > 1 ? `(${v.quantidade}x)` : ""}
                    </div>
                    <div className="summary-vehicle-detail">
                      Peso: {v.peso || pesoReferencia || formData.peso || "—"} kg
                      {freteVeiculo != null && (
                        <span className="summary-vehicle-price">
                          · {formatCurrency(freteVeiculo)}
                        </span>
                      )}
                    </div>
                    {i === 0 && (
                      <div className="summary-vehicle-detail summary-vehicle-detail--subtle">
                        Seguro incluso
                      </div>
                    )}
                  </div>
                );
              })}
            </SummarySection>
          )}

          {step === 3 && transportadoraEscolhida && (
            <SummarySection title="Escolha final">
              <div className="summary-item">
                <div className="summary-item-label">Transportadora</div>
                <Val>{transportadoraEscolhida.transportadora}</Val>
              </div>
            </SummarySection>
          )}

          {step >= 2 && servicos.length > 0 && (
            <SummarySection title="Serviços">
              {servicos.map((s, i) => (
                <div className="summary-item" key={i}>
                  <div className="summary-item-label">{s.tipoServico || "Serviço"}</div>
                  <Val empty={!s.valor}>{s.valor}</Val>
                </div>
              ))}
            </SummarySection>
          )}

          {(hasSimulated || step === 3) && freteTotal != null && (
            <div className={`summary-total-block ${transportadoraEscolhida ? "confirmed" : ""}`}>
              <div className="summary-total-label">
                {transportadoraEscolhida ? "Frete Total" : "Melhor Estimativa"}
              </div>
              <div className="summary-total-value">{formatCurrency(freteTotal)}</div>
              {somaServicos > 0 && (
                <div className="summary-vehicle-detail summary-vehicle-detail--subtle">
                  Inclui {formatCurrency(somaServicos)} em serviços
                </div>
              )}
            </div>
          )}

          {step < 2 && (
            <div className="summary-panel-note" style={autopilotInsetCardStyle}>
              Preencha os dados e avance para ver a simulação de frete.
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default SummaryPanel;
